import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import omit from 'lodash/omit'

import { useApi, useDashboard, useLocalStorage, useSessionStorage } from '@/hooks'
import { duration } from '@/insights/common/formatUtils'
import { type DataSource, DEFAULT_DATA_SOURCE } from '@/types/data-source'

import { AssetStateSummary, StatusColumn } from './types'
import { Coordinate, getUSBounds, getResponsiveUSZoom, boundsFromAllPoints } from './utils'

import { AssetState } from '../../../types/asset-types'

export type LayoutMode = 'map' | 'table' | 'split'

export type ColumnSnapshot = Array<
  Pick<StatusColumn, 'selectedColumnsDatabaseName' | 'selected' | 'displayOrder' | 'width'>
>
interface SidebarState {
  sidebarWidth: number
  limitToMap: boolean
  viewMode: 'list' | 'details'
  layoutMode: LayoutMode
  splitRatio: number
  mapPanelWidth: number
}

export type MapStyle = 'road' | 'satellite' | '3d'

interface MapState {
  bounds: {
    north: number
    south: number
    east: number
    west: number
  }
  pitch: number
  bearing: number
  zoom: number
  focus: boolean
  showQuickView: boolean
  style: MapStyle
  clustersEnabled: boolean
  assetLabels: boolean
  severitySource: 'alarms' | 'alerts'
  zOrders: number[] // Array of zOrder values to show
  geofenceCategoryIds: number[] // Array of category IDs to show
}

type GeofenceFilter = 'all' | 'inside' | 'outside'
type PowerFilter = 'all' | 'on' | 'off'
type GatewayFilter = 'all' | 'online' | 'offline'
export type AlarmSeverityFilter = {
  noAlarms: boolean
  critical: boolean
  warning: boolean
  informational: boolean
}
export type AlertSeverityFilter = {
  noAlerts: boolean
  critical: boolean
  warning: boolean
  informational: boolean
}
type DoorFilter = 'all' | 'open' | 'closed'

/*
type Alert = {
  id: number
  name: string
}
*/

export interface Filters {
  companyId?: number
  geofence: GeofenceFilter
  power: PowerFilter
  gateway: GatewayFilter
  severity: AlarmSeverityFilter
  alertSeverity: AlertSeverityFilter
  door: DoorFilter
  alarmCodes: Set<number>
  alerts: Set<string>
  alarmsActiveSince: string | null
  alertsActiveSince: string | null
}

interface MapContextType {
  /** Which back end serves this page's asset data while Fresh and Timescale run in parallel. */
  dataSource: DataSource
  setDataSource: (value: DataSource) => void
  searchTerm: string
  setSearchTerm: (term: string) => void
  windowOffset: number
  setWindowOffset: React.Dispatch<React.SetStateAction<number>>
  allAssets: AssetStateSummary[]
  filteredAssets: AssetStateSummary[]
  viewportFilteredAssets: AssetStateSummary[]
  geojsonData: GeoJSON.GeoJSON
  currentAssetData?: AssetState
  setCurrentAssetId: (assetId: number | undefined) => void
  currentAssetId?: number
  animatedAssetLocation?: Coordinate
  showAssetModal: boolean
  setShowAssetModal: (show: boolean) => void
  sidebarState: SidebarState
  setSidebarState: React.Dispatch<React.SetStateAction<SidebarState>>
  mapState: MapState
  setMapState: React.Dispatch<React.SetStateAction<MapState>>
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  isLoadingAssets: boolean
  error: string
  updatedAt?: Date
  reset: () => void
  isReady: boolean
  setIsReady: (ready: boolean) => void
  sidebarHydrated: boolean
  mapHydrated: boolean
  filtersHydrated: boolean
  setSideBarWidth: (width: number) => void
  selectedSavedMap: SaveableAssetMapContext | null
  setSelectedSavedMap: (map: SaveableAssetMapContext | null) => void
  isSwitchingView: boolean
  setIsSwitchingView: (v: boolean) => void
  filtersResetCount: number
  assetDetailSheetIsOpen: boolean
  setAssetDetailSheetIsOpen: (v: boolean) => void
}

export interface SaveableAssetMapContext {
  name: string
  companyId: number
  sidebarState: SidebarState
  mapState: MapState
  filters: Filters
  isDefault?: boolean
  columns?: ColumnSnapshot
}

const MapContext = createContext<MapContextType | undefined>(undefined)

const defaultMapState = {
  pitch: 30,
  bearing: 90,
  zoom: getResponsiveUSZoom(),
  style: 'road' as const,
  bounds: getUSBounds(6),
  clustersEnabled: true,
  assetLabels: false,
  severitySource: 'alarms' as const,
  zOrders: [],
  geofenceCategoryIds: [], // Will be set when categories are loaded
  focus: false,
  track: false,
  showQuickView: true,
}

export const defaultSidebarState: SidebarState = {
  limitToMap: false,
  sidebarWidth: 400,
  viewMode: 'list',
  layoutMode: 'map',
  splitRatio: 0.5,
  mapPanelWidth: 380,
}

const defaultFilters: Filters = {
  gateway: 'all' as const,
  geofence: 'all' as const,
  power: 'all' as const,
  door: 'all' as const,
  severity: {
    noAlarms: true,
    critical: true,
    informational: true,
    warning: true,
  },
  alertSeverity: {
    noAlerts: true,
    critical: true,
    informational: true,
    warning: true,
  },
  alarmCodes: new Set<number>(),
  alerts: new Set<string>(),
  alarmsActiveSince: null,
  alertsActiveSince: null,
}

type FiltersStored = Omit<Filters, 'alarmCodes' | 'alerts'> & {
  alarmCodes: number[]
  alerts: string[]
}

const serializeFilters = (f: Filters): string =>
  JSON.stringify({
    ...f,
    alarmCodes: Array.from(f.alarmCodes),
    alerts: Array.from(f.alerts),
  } satisfies FiltersStored)

const deserializeFilters = (raw: string): Filters => {
  const parsed = JSON.parse(raw) as FiltersStored
  return {
    ...parsed,
    alarmCodes: new Set(parsed.alarmCodes),
    alerts: new Set(parsed.alerts),
  }
}

const SESSION_KEYS = {
  sidebarState: 'assetMap.sidebarState',
  mapState: 'assetMap.mapState',
  filters: 'assetMap.filters',
  searchTerm: 'assetMap.searchTerm',
} as const

const fullAssetCache = new Map<string, AssetState & { cacheTime: Date }>()

export const MapProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { api } = useApi()
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string>('')
  const [isLoadingAssets, setIsLoadingAssets] = useState(true)
  const [allAssets, setAllAssets] = useState<AssetStateSummary[]>([])
  const [currentAssetId, _setCurrentAssetId] = useState<number | undefined>()
  const [currentAssetData, setCurrentAssetData] = useState<AssetState | undefined>()
  const [sidebarState, setSidebarState, sidebarHydrated] = useSessionStorage<SidebarState>(
    SESSION_KEYS.sidebarState,
    defaultSidebarState
  )
  const [mapState, setMapState, mapHydrated] = useSessionStorage<MapState>(SESSION_KEYS.mapState, defaultMapState)
  const [filters, setFilters, filtersHydrated] = useSessionStorage<Filters>(SESSION_KEYS.filters, defaultFilters, {
    serialize: serializeFilters,
    deserialize: deserializeFilters,
  })
  // Deliberately local rather than part of a saved view: a tester's choice of back end
  // should survive a reload but must never travel to another user through a shared map.
  const [dataSource, setDataSource] = useLocalStorage<DataSource>('assetMapDataSource', DEFAULT_DATA_SOURCE)
  const [searchTerm, setSearchTerm] = useSessionStorage<string>(SESSION_KEYS.searchTerm, '')
  const [windowOffset, setWindowOffset] = useState(0)
  const [showAssetModal, setShowAssetModal] = useState(false)
  const [updatedAt, setUpdatedAt] = useState<Date>()
  const [animatedAssetLocation, setAnimatedAssetLocation] = useState<Coordinate | undefined>()
  const cancelAnimationRef = useRef<(() => void) | null>(null)
  const { configuration } = useDashboard()
  const [updatingSidebarWidth, setUpdatingSidebarWidth] = useState<number>(0) // dummy state to trigger useEffect for saving sidebar width
  const [selectedSavedMap, setSelectedSavedMap] = useState<SaveableAssetMapContext | null>(null)
  const [isSwitchingView, setIsSwitchingView] = useState(false)
  const [filtersResetCount, setFiltersResetCount] = useState(0)
  const [assetDetailSheetIsOpen, setAssetDetailSheetIsOpen] = useState(false)

  const setCurrentAssetId = useCallback((id: number | undefined) => {
    _setCurrentAssetId(id)
    if (id !== undefined) {
      setMapState((prev) => ({ ...prev, showQuickView: true }))
    } else {
      // With nothing selected (a click on empty map, a company change) the detail sheet has
      // no asset to show; left open it would sit on a spinner.
      setAssetDetailSheetIsOpen(false)
    }
  }, [])

  useEffect(() => {
    if (sidebarHydrated) return
    // dashboard config has the initial value, so let's use it instead of doing another db call.
    setSidebarState((prev) => ({
      ...prev,
      sidebarWidth: configuration.assetMapSideBarWidth ?? defaultSidebarState.sidebarWidth,
    }))
  }, [configuration.assetMapSideBarWidth, sidebarHydrated])

  useEffect(() => {
    if (mapHydrated) return
    const defaultZoom = 6
    const bounds = getUSBounds(defaultZoom)
    setMapState((prev) => ({
      ...prev,
      bounds,
    }))
  }, [mapHydrated])

  useEffect(() => {
    console.debug('[Load] Effect triggered', { companyId: filters.companyId, isReady })
    if (!isReady) {
      return
    }
    if (!filters.companyId) {
      setAllAssets([]) // Clear assets immediately
      setIsLoadingAssets(false)
      return
    }

    // Clear assets and selection immediately when company changes
    console.debug('[Load] Starting load for company', filters.companyId, performance.now().toFixed(0))
    setAllAssets([])
    setCurrentAssetId(undefined)
    setCurrentAssetData(undefined)
    setAnimatedAssetLocation(undefined)
    setIsLoadingAssets(true)
    setError('')

    let timeoutId: NodeJS.Timeout | null = null
    let cancelled = false

    const fetchAssets = async (zoom: boolean) => {
      if (!api) {
        return
      }
      const t0 = performance.now()
      console.debug('[Load] API call start', t0.toFixed(0))
      const params = new URLSearchParams({
        companyId: String(filters.companyId),
        skip: '0',
        take: '10000',
        deleted: 'false',
      })
      if (filters.alarmsActiveSince) params.append('alarmsActiveSince', filters.alarmsActiveSince)
      if (filters.alertsActiveSince) params.append('alertsActiveSince', filters.alertsActiveSince)
      // Consumed and stripped by resolveApiPath — never reaches the server.
      params.append('dataSource', dataSource)
      const path = `/assets/state/summary?${params.toString()}`
      const response = await api.get<AssetStateSummary[]>({ path, origin: 'AssetMap' })
      const t1 = performance.now()
      console.debug('[Load] API call done', t1.toFixed(0), `(${((t1 - t0) / 1000).toFixed(2)}s)`)

      // Don't update state if the effect was cleaned up
      if (cancelled) {
        console.debug('[Load] Cancelled, ignoring response')
        return
      }

      if (response.error) {
        setError(response.message ?? 'An unexpected error occurred')
        setAllAssets([])
      } else if (response.data?.length) {
        console.debug('[Load] Setting', response.data.length, 'assets', performance.now().toFixed(0))
        setUpdatedAt(new Date())
        setAllAssets(response.data)
        console.debug('[Load] setAllAssets done', performance.now().toFixed(0))
        setError('')
        if (zoom) {
          console.debug('[Load] Calculating zoom', performance.now().toFixed(0))
          const points = response.data.map((a) => ({ latitude: a.location.latitude, longitude: a.location.longitude }))
          const { bounds, zoom } = boundsFromAllPoints(points)
          setMapState((prev) => ({ ...prev, bounds, zoom }))
          console.debug('[Load] Zoom done', performance.now().toFixed(0))
        }
      } else {
        setError('No assets found')
      }
      console.debug('[Load] Setting isLoading=false', performance.now().toFixed(0))
      setIsLoadingAssets(false)

      // Schedule next refresh after this one completes
      if (!cancelled) {
        timeoutId = setTimeout(() => fetchAssets(false), 60 * 1000)
      }
    }

    fetchAssets(true)

    return () => {
      cancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [api, filters.companyId, filters.alarmsActiveSince, filters.alertsActiveSince, isReady, dataSource])

  // Fetch asset data when selected (initial fetch only)
  useEffect(() => {
    // Clean up any running animation when currentAssetId changes
    if (cancelAnimationRef.current) {
      cancelAnimationRef.current()
      cancelAnimationRef.current = null
    }

    if (!currentAssetId) {
      setCurrentAssetData(undefined)
      setAnimatedAssetLocation(undefined)
      return
    }

    // Clear previous asset data if switching to a different asset
    setCurrentAssetData((prev) => (prev && prev?.id !== currentAssetId ? undefined : prev))
    setAnimatedAssetLocation(undefined)

    // Keyed by back end as well as asset: the same asset has a separate cache entry per
    // data source, so switching sources can't show the other one's stale row.
    const cacheKey = `${dataSource}:${String(currentAssetId)}`

    // Check cache first for immediate display
    if (fullAssetCache.has(cacheKey)) {
      const cached = fullAssetCache.get(cacheKey)
      const cutoff = new Date(new Date().getTime() - 60 * 1000)
      if (cached && cached.name && cached.cacheTime > cutoff) {
        setCurrentAssetData(omit(cached, 'cacheTime'))
        setAnimatedAssetLocation({
          latitude: cached.location.latitude,
          longitude: cached.location.longitude,
        })
      }
    }

    // Get the initial location from allAssets for the starting point
    const summaryAsset = allAssets.find((a) => a.id === currentAssetId)
    if (summaryAsset) {
      setAnimatedAssetLocation({
        latitude: summaryAsset.location.latitude,
        longitude: summaryAsset.location.longitude,
      })
    }

    const fetchAssetData = async () => {
      try {
        const assetState = await api.simpleGet<AssetState>({
          path: `assets/state/${currentAssetId}?dataSource=${dataSource}`,
          origin: 'AssetMap',
        })
        fullAssetCache.set(cacheKey, { ...assetState, cacheTime: new Date() })

        // Set location immediately (no animation for initial fetch)
        setAnimatedAssetLocation({
          latitude: assetState.location.latitude,
          longitude: assetState.location.longitude,
        })

        setCurrentAssetData(assetState)
        setError('')
      } catch (ex) {
        console.error(ex)
        fullAssetCache.delete(cacheKey)
        setError('Could not load asset')
        setCurrentAssetData(undefined)
        setShowAssetModal(false)
      }
    }

    // Initial fetch only
    fetchAssetData()

    return () => {
      if (cancelAnimationRef.current) {
        cancelAnimationRef.current()
        cancelAnimationRef.current = null
      }
    }
  }, [allAssets, currentAssetId, dataSource])

  // Track mode: Poll every 5 seconds and animate smoothly between positions
  const flattenValue = (value: unknown): string => {
    //decision treee here
    if (typeof value === 'boolean') {
      return ''
    }
    if (typeof value === 'string') {
      return value.toLowerCase()
    }
    if (typeof value === 'number') {
      return value.toString()
    }
    if (value === null || value === undefined) {
      return ''
    }
    if (Array.isArray(value)) {
      return value.map((v) => flattenValue(v)).join(' ')
    }
    if (typeof value === 'object') {
      return Object.values(value)
        .map((v) => flattenValue(v))
        .join(' ')
    }
    return ''
  }

  const assetSearchIndex = useMemo(() => {
    const map = new Map<number, string>()
    for (const asset of allAssets) {
      map.set(asset.id, flattenValue(asset).toLowerCase())
    }
    return map
  }, [allAssets])

  const filteredAssets = useMemo(() => {
    return allAssets.filter((asset) => {
      if (searchTerm) {
        const blob = assetSearchIndex.get(asset.id)
        if (!blob?.includes(searchTerm.toLowerCase())) {
          return false
        }
      }

      // Geofence filter
      if (filters.geofence === 'inside' && !asset.insideGeofence) return false
      if (filters.geofence === 'outside' && asset.insideGeofence) return false

      // Power filter
      if (filters.power === 'on' && !asset.powerOn) return false
      if (filters.power === 'off' && asset.powerOn) return false

      // Gateway filter
      if (filters.gateway === 'online' && !asset.gatewayOn) return false
      if (filters.gateway === 'offline' && asset.gatewayOn) return false

      // Alarm filter
      const hasNoAlarms = asset.highestSeverity === null
      const hasCritical = asset.highestSeverity === 'critical'
      const hasWarning = asset.highestSeverity === 'warning'
      const hasInformational = asset.highestSeverity === 'informational'

      // Check if asset's severity is allowed
      const severityMatch =
        (filters.severity.noAlarms && hasNoAlarms) ||
        (filters.severity.critical && hasCritical) ||
        (filters.severity.warning && hasWarning) ||
        (filters.severity.informational && hasInformational)

      if (!severityMatch) return false

      // Alarm code filter - assets with no alarms already passed via noAlarms above
      if (!hasNoAlarms) {
        if (filters.alarmCodes.size > 0) {
          const hasMatchingCode = asset.activeAlarmCodeIds?.some((id: number) => filters.alarmCodes.has(id))
          if (!hasMatchingCode) return false
        } else {
          // No alarm codes selected, hide assets that have alarms
          return false
        }
      }

      // Alert filter
      const hasNoAlerts = asset.highestAlertSeverity === null
      const hasAlertCritical = asset.highestAlertSeverity === 'critical'
      const hasAlertWarning = asset.highestAlertSeverity === 'warning'
      const hasAlertInformational = asset.highestAlertSeverity === 'informational'

      const alertSeverityMatch =
        (filters.alertSeverity.noAlerts && hasNoAlerts) ||
        (filters.alertSeverity.critical && hasAlertCritical) ||
        (filters.alertSeverity.warning && hasAlertWarning) ||
        (filters.alertSeverity.informational && hasAlertInformational)

      if (!alertSeverityMatch) return false

      // Alert name filter - assets with no alerts already passed via noAlerts above
      if (!hasNoAlerts) {
        if (filters.alerts.size > 0) {
          const hasMatchingAlert = asset.activeAlerts?.some((a) => filters.alerts.has(a.name))
          if (!hasMatchingAlert) return false
        } else {
          return false
        }
      }

      // Door filter
      if (filters.door === 'open' && !asset.hasOpenDoor) return false
      if (filters.door === 'closed' && asset.hasOpenDoor) return false

      return true
    })
  }, [allAssets, filters, searchTerm, filtersResetCount, assetSearchIndex])

  const viewportFilteredAssets = useMemo(() => {
    if (!sidebarState.limitToMap || !mapState.bounds || sidebarState.layoutMode === 'table') {
      return filteredAssets
    }

    // Filter to only assets within current map viewport
    return filteredAssets.filter((asset) => {
      const { latitude, longitude } = asset.location
      return (
        latitude <= mapState.bounds.north &&
        latitude >= mapState.bounds.south &&
        longitude <= mapState.bounds.east &&
        longitude >= mapState.bounds.west
      )
    })
  }, [filteredAssets, mapState.bounds, sidebarState])

  // Convert assets to GeoJSON format for Mapbox clustering
  const geojsonData = useMemo(() => {
    return {
      type: 'FeatureCollection' as const,
      features: viewportFilteredAssets.map((asset) => {
        const highestSeverity =
          mapState.severitySource === 'alerts' ? asset.highestAlertSeverity : asset.highestSeverity
        // Map severity to numeric value for cluster aggregation
        const severityValue =
          highestSeverity === 'critical'
            ? 2
            : highestSeverity === 'warning'
              ? 1
              : highestSeverity === 'informational'
                ? 0
                : -1

        return {
          type: 'Feature' as const,
          id: asset.id,
          geometry: {
            type: 'Point' as const,
            coordinates:
              currentAssetData?.id === asset.id
                ? [currentAssetData.location.longitude, currentAssetData.location.latitude]
                : [asset.location.longitude, asset.location.latitude],
          },
          properties: {
            id: asset.id,
            name: asset.gatewayOn ? asset.name : `${asset.name}\n(${duration(asset.umdSampleTime)})`,
            gatewayOn: asset.gatewayOn,
            powerOn: asset.powerOn,
            highestSeverity: highestSeverity || 'none',
            severityValue: severityValue,
            selected: asset.id === currentAssetId,
            // Note: Not storing full asset data here for performance
          },
        }
      }),
    }
  }, [viewportFilteredAssets, currentAssetData, currentAssetId, mapState.severitySource])

  const reset = () => {
    Object.values(SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key))
    if (allAssets.length > 0) {
      const points = allAssets.map((a) => ({
        latitude: a.location.latitude,
        longitude: a.location.longitude,
      }))
      const { bounds, zoom } = boundsFromAllPoints(points)
      setMapState({ ...defaultMapState, bounds, zoom })
    } else {
      setMapState(defaultMapState)
    }
    setSidebarState(defaultSidebarState)
    setFilters((prev) => ({ ...defaultFilters, companyId: prev.companyId }))
    setSearchTerm('')
    setCurrentAssetId(undefined)
    setCurrentAssetData(undefined)
    setAnimatedAssetLocation(undefined)
    setShowAssetModal(false)
    setFiltersResetCount((c) => c + 1)
  }

  const setSideBarWidth = (width: number) => {
    // update the DB and global config
    setSidebarState((prev) => ({ ...prev, sidebarWidth: width }))
    setUpdatingSidebarWidth(width)
  }

  // debounce saving sidebar width to DB to avoid excessive calls while dragging
  useEffect(() => {
    if (!api || updatingSidebarWidth === 0) {
      // not initialized yet
      return
    }
    const timeout = setTimeout(() => {
      api
        .put({
          path: `app/profiles/sidebar-width`,
          origin: 'AssetMap',
          body: { assetMapSideBarWidth: updatingSidebarWidth },
        })
        .catch((e) => {
          console.error('Failed to save sidebar width', e)
        })
    }, 500)

    return () => clearTimeout(timeout)
  }, [updatingSidebarWidth, api])

  const value = useMemo(
    () => ({
      isReady,
      setIsReady,
      error,
      dataSource,
      setDataSource,
      searchTerm,
      setSearchTerm,
      windowOffset,
      setWindowOffset,
      isLoadingAssets,
      allAssets,
      filteredAssets,
      viewportFilteredAssets,
      geojsonData,
      currentAssetId,
      setCurrentAssetId,
      animatedAssetLocation,
      showAssetModal,
      setShowAssetModal,
      currentAssetData,
      setCurrentAssetData,
      mapState,
      setMapState,
      sidebarState,
      setSidebarState,
      filters,
      setFilters,
      sidebarHydrated,
      mapHydrated,
      filtersHydrated,
      updatedAt,
      reset,
      setSideBarWidth,
      selectedSavedMap,
      setSelectedSavedMap,
      isSwitchingView,
      setIsSwitchingView,
      filtersResetCount,
      assetDetailSheetIsOpen,
      setAssetDetailSheetIsOpen,
    }),
    [
      isReady,
      setIsReady,
      error,
      dataSource,
      setDataSource,
      searchTerm,
      setSearchTerm,
      windowOffset,
      setWindowOffset,
      isLoadingAssets,
      allAssets,
      filteredAssets,
      viewportFilteredAssets,
      geojsonData,
      currentAssetId,
      setCurrentAssetId,
      animatedAssetLocation,
      showAssetModal,
      setShowAssetModal,
      currentAssetData,
      setCurrentAssetData,
      mapState,
      setMapState,
      sidebarState,
      setSidebarState,
      filters,
      setFilters,
      sidebarHydrated,
      mapHydrated,
      filtersHydrated,
      updatedAt,
      reset,
      setSideBarWidth,
      selectedSavedMap,
      setSelectedSavedMap,
      isSwitchingView,
      setIsSwitchingView,
      filtersResetCount,
      assetDetailSheetIsOpen,
      setAssetDetailSheetIsOpen,
    ]
  )

  // console.log({ companyId, allAssets, value })
  return <MapContext.Provider value={value}>{children}</MapContext.Provider>
}

export const useMapContext = () => {
  const ctx = useContext(MapContext)
  if (!ctx) {
    throw new Error('useMapContext must be used within MapProvider')
  }
  return ctx
}
